import { useLanguage } from '../context/LanguageContext';
import translations from './translations';

/**
 * Custom hook to get translated text based on current language
 * @returns {Object} Object containing translation functions and current language
 */
export const useTranslation = () => {
  const { language } = useLanguage();
  
  /**
   * Function to get translated text
   * @param {string} key - The translation key to look up
   * @param {Object} [params] - Optional parameters for variable interpolation
   * @returns {string} The translated text
   */
  const t = (key, params = {}) => {
    try {
      // Map display language names to translation keys
      const languageMap = {
        'English': 'en',
        'Hindi': 'hi'
      };
      
      const translationKey = languageMap[language] || 'en';
      
      // Check if language exists in translations
      if (!translations[translationKey]) {
        console.warn(`Language '${language}' not found in translations, falling back to English`);
        const text = translations.en[key] || key;
        return interpolate(text, params);
      }
      
      // If the key doesn't exist in the current language, fall back to English
      if (!translations[translationKey][key]) {
        console.warn(`Translation key '${key}' not found for language '${language}', falling back to English`);
        const text = translations.en[key] || key;
        return interpolate(text, params);
      }
      
      return interpolate(translations[translationKey][key], params);
    } catch (error) {
      console.error(`Error in translation for key '${key}':`, error);
      return key; // Return the key as fallback in case of errors
    }
  };
  
  /**
   * Helper function to interpolate variables in translation strings
   * @param {string} text - The text containing placeholders like {{variable}}
   * @param {Object} params - The variables to interpolate
   * @returns {string} The interpolated text
   */
  const interpolate = (text, params) => {
    if (!params || Object.keys(params).length === 0 || !text) {
      return text;
    }
    
    return Object.keys(params).reduce((result, key) => {
      const regex = new RegExp(`\{\{${key}\}\}`, 'g');
      return result.replace(regex, params[key]);
    }, text);
  };
  
  return { t, language };
};